export type ColumnType = 'string' | 'number' | 'date' | 'boolean';
export type DateGranularity = 'month' | 'week' | 'quarter';

export type QualityReportingStatus = 'QRP_SUBMITTER' | 'QRP_NON_SUBMITTER';

export interface ReportParameters {
  quality_reporting_status?: QualityReportingStatus | null;
}

// A serve-time DERIVED column. Date-relative columns read one visible source;
// parameter_case is the fixed, registry-owned QRP projection (Expected revenue,
// Adjustment, the Medicare-priced columns). The discriminated recipe stops a new
// derived column from falling through to a raw Parquet reference.
export type DerivedFrom =
  | { kind: 'days_since' | 'days_until'; column: string }
  | {
      kind: 'parameter_case';
      parameter: 'quality_reporting_status';
      cases:
        | {
            QRP_SUBMITTER: 'expected_revenue_submitter';
            QRP_NON_SUBMITTER: 'expected_revenue_non_submitter';
          }
        | {
            QRP_SUBMITTER: 'adjustment_submitter';
            QRP_NON_SUBMITTER: 'adjustment_non_submitter';
          }
        | {
            QRP_SUBMITTER: 'medicare_priced_expected_submitter';
            QRP_NON_SUBMITTER: 'medicare_priced_expected_non_submitter';
          }
        | {
            QRP_SUBMITTER: 'pricing_change_submitter';
            QRP_NON_SUBMITTER: 'pricing_change_non_submitter';
          }
        | {
            QRP_SUBMITTER: 'unexplained_variance_submitter';
            QRP_NON_SUBMITTER: 'unexplained_variance_non_submitter';
          };
    };

export interface DatasetColumn {
  name: string;
  label: string;
  type: ColumnType;
  default_buckets?: number[];
  default_granularity?: DateGranularity;
  derived_from?: DerivedFrom;
}

export interface DatasetRelationship {
  target: string;
  label: string;
  local_key: string;
  target_key: string;
  cardinality: string; // v1: only 'many_to_one' is compilable
}

export interface DatasetManifestEntry {
  key: string;
  label: string;
  /**
   * Key of the dataset whose permission umbrella this one belongs to; absent
   * means it stands alone. Saved reports are badged with the umbrella's label
   * ("Episodes"), while the builder's picker keeps this dataset's own label.
   */
  umbrella_key?: string;
  version: number;
  columns: DatasetColumn[];
  relationships?: DatasetRelationship[];
  /**
   * Axes a windowed `scope=primary` can actually be pruned on for this caller
   * (zone-mapped AND served). Advisory: the server still accepts any axis and
   * fails open to the full dataset on one it cannot prune, so this only decides
   * whether we mint a per-window client cache entry that would hold identical
   * bytes anyway.
   *
   * Optional because an older server omits it: `undefined` means "no
   * information, keep the legacy derivation" (window on any date column), while
   * `[]` means "nothing on this dataset is prunable".
   */
  prunable_axes?: string[];
  /**
   * Which clinics' rows the server actually served this caller from this
   * dataset. Display only — the filtering already happened server-side; this
   * exists so the UI can say what the numbers cover, because a filtered total
   * is indistinguishable from an unfiltered one.
   *
   * Optional because an older server omits it, and absence must read as
   * "unbounded" — the behaviour before clinic row filtering existed — rather
   * than as a scope we would then state wrongly.
   */
  clinic_scope?: ClinicScope;
}

export type ClinicScope =
  | { kind: 'agency'; clinic_names?: string[]; clinic_ids?: string[] }
  // clinic_ids is the scope's identity (names can collide between clinics);
  // optional because a manifest minted before the field existed omits it.
  | { kind: 'clinics'; clinic_names: string[]; clinic_ids?: string[] };

export interface ReportingManifest {
  datasets: DatasetManifestEntry[];
  /** Optional for older servers; support schemas are never ordinary picker data. */
  pipeline_datasets?: DatasetManifestEntry[];
}

export type BucketTransform = { type: 'bucket'; edges: number[] };
export type DateTruncTransform = { type: 'date_trunc'; unit: DateGranularity };
export type DimensionTransform = BucketTransform | DateTruncTransform;

export interface GroupBy {
  column: string;
  transform?: DimensionTransform;
}

export type Agg = 'count' | 'sum' | 'avg' | 'min' | 'max' | 'share';

export interface Measure {
  agg: Agg;
  column?: string; // undefined for count
  plus?: string; // share only: ratio is column / (column + plus)
}

export type FilterOp =
  | 'eq'
  | 'neq'
  | 'in'
  | 'not_in'
  | 'contains'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'between'
  | 'is_null'
  | 'not_null'
  | 'relative_date';

export interface ReportFilter {
  column: string;
  op: FilterOp;
  value?: unknown; // op-specific: scalar | [lo, hi] | values[] | { last_days: number } | { next_days: number }
}

export type ReportView = 'table' | 'pivot' | 'bar';
export type DetailColumnsMode = 'all' | 'custom';

// Authored chart config for a DETAIL report (only meaningful when
// `group_bys` is empty — a grouped report charts via its own group_bys/
// measures instead). `column` is required iff `agg === 'sum'` (omitted for
// `count`). The backend validates that `group_by`/`column` are among the
// definition's selected `columns`.
export interface ReportChartConfig {
  group_by: string;
  agg: 'sum' | 'count';
  column?: string;
}

export interface DisplayColumn {
  label: string;
  fields: string[];
  emphasis?: boolean;
  emphasized_fields?: string[];
}

export interface PatientRosterPipeline {
  type: 'patient_roster';
  version: 1;
  support_datasets: ['patient-roster-visits', 'patient-roster-visit-diagnoses'];
  report_start: string;
  report_end: string;
  clinic_id?: string | null;
}

export interface PcrPipeline {
  type: 'pcr';
  version: 1;
}

export type ReportPipeline = PatientRosterPipeline | PcrPipeline;

export interface ReportDefinition {
  // 1 = primary-only; 2 = references related datasets (dotted refs). Version 3
  // adds detail_columns_mode; version 4 adds reusable grouped display columns.
  schema_version: 1 | 2 | 3 | 4;
  // Version 4 presentation metadata. Missing metadata preserves the legacy
  // one-field-per-visible-column behavior, including for definitions v1-v3.
  display_columns?: DisplayColumn[];
  pipeline?: ReportPipeline;
  // Omitted by legacy definitions. Backend validation requires it for version 3
  // and rejects it for versions 1 and 2.
  detail_columns_mode?: DetailColumnsMode;
  dataset_key: string;
  dataset_version: number;
  // Per-related-dataset version stamps. Backend-enforced contract: present
  // iff the definition is joined, keys exactly the referenced related
  // datasets (stampDefinitionVersions keeps it true). Drift is detected
  // structurally at open (live manifests) and at run time (the runner's
  // extract-schema gate) — see useReportRunner.
  related_versions?: Record<string, number>;
  // Explicit, ordered list of related dataset keys joined into the base — the
  // user adds a join like they add a filter, and its columns then become
  // selectable. Each key must be a declared many-to-one relationship target of
  // the base dataset AND permitted for the caller. A dataset stays joined (and
  // is LEFT JOINed by the compiler) even when no column from it is currently
  // selected — the join is explicit intent, not a byproduct of a column ref.
  // Optional for back-compat: legacy definitions saved by the per-column UI
  // omit it; backfillJoinedDatasets derives it from their dotted refs on load.
  // `undefined` = not yet backfilled (legacy); `[]` = explicitly no joins.
  joined_datasets?: string[];
  // Custom column names keyed by result column key; a missing key uses the default name.
  column_labels?: Record<string, string>;
  // Legacy/v3-all: empty = all primary manifest columns. v3-custom: the
  // complete explicit detail selection, where empty is deliberately no columns.
  columns: string[];
  group_bys: GroupBy[];
  measures: Measure[];
  pivot_column?: GroupBy | null;
  filters: ReportFilter[];
  sort: { by: string; dir: 'asc' | 'desc' }[];
  view: ReportView;
  // Population opt-ins. Optional so legacy/system definitions keep their raw
  // shape; every read treats a missing key as false.
  include_test_rows?: boolean;
  include_inactive_episodes?: boolean;
  include_imported_patients?: boolean;
  // Opts back in to invoice-billed claims on claims and billing-cycles data,
  // which an agency may have asked its AR reports to leave out.
  include_invoice_claims?: boolean;
  // Set on the four AR system reports: the agency's setting to leave invoice
  // claims off AR applies to a definition only when it carries this key.
  exclude_invoice_claims_by_agency?: boolean;
  // Table-view subtotal rows per leading group-by value. Optional and absent
  // by default (legacy definitions omit it); meaningful only with >= 2
  // group-bys and an additive (count/sum) measure — the table gates on both.
  subtotals?: boolean;
  // Authored chart config for a DETAIL report (no group_bys); optional and
  // absent by default. `validateDefinition` drops it when stale (group_by/
  // column no longer among the selected columns), which normalizes `view`
  // back to 'table' for a detail report on open.
  chart?: ReportChartConfig | null;
  // Optional report-level calculation input. When omitted or undefined,
  // quality_reporting_status defaults to QRP_SUBMITTER for the calculation.
  // Explicit null specifies All (rendering expected revenue as blank/null).
  parameters?: ReportParameters;
  // Opt-in zero-filled BUSINESS-DAY spine for exact-date grouped reports.
  // Zero-fills Monday–Friday within the bounded window; any date actually
  // present in the aggregate — including weekends — always renders, so no
  // dollar is ever dropped by the spine. Optional and absent by default;
  // ignored unless compileReport's full trigger holds (single untransformed
  // date group-by, all-additive measures, only bound-representable filters on
  // the grouped column, and a resolved window no wider than
  // MAX_DATE_CATEGORIES CALENDAR days) — otherwise the report renders exactly
  // as it would with gap_fill absent. An empty window still renders "no
  // results", never a grid of $0 rows.
  gap_fill?: boolean;
}

export type ReportPopulationSetting =
  | 'include_test_rows'
  | 'include_inactive_episodes'
  | 'include_imported_patients'
  | 'include_invoice_claims';

export interface ReportListItem {
  id: string | null;
  system_key: string | null;
  name: string;
  description: string | null;
  dataset_key: string;
  source: 'system' | 'agency';
  created_by: string | null;
  created_by_name: string | null;
  created_at: string | null;
  updated_at: string | null;
  definition: ReportDefinition;
  visibility: 'private' | 'agency' | null;
  // Made for a dashboard widget; the Reports page lists it apart.
  widget_only?: boolean;
}

export interface ReportDefinitionPayload {
  name: string;
  description?: string | null;
  dataset_key: string;
  definition: ReportDefinition;
  visibility?: 'private' | 'agency';
  widget_only?: boolean;
}

// Highest definition schema_version this build understands. The builder
// refuses to open (read-only-refuse, not silently degrade) any definition
// newer than this, so a future shape change can't be destructively re-saved
// by an out-of-date tab.
export const SUPPORTED_SCHEMA_VERSION = 4;

export function emptyDefinition(
  datasetKey: string,
  datasetVersion: number
): ReportDefinition {
  return {
    schema_version: 3,
    detail_columns_mode: 'all',
    dataset_key: datasetKey,
    dataset_version: datasetVersion,
    columns: [],
    group_bys: [],
    measures: [],
    pivot_column: null,
    filters: [],
    sort: [],
    view: 'table',
  };
}

export type WidgetForm = 'number' | 'bar' | 'line' | 'pie' | 'table';

export interface ReportWidgetDisplay {
  measure?: string | null;
  subtext_measure?: string | null;
  row_limit?: number | null;
}

export interface ReportWidget {
  id: string;
  name: string;
  report_definition_id: string | null;
  system_report_key: string | null;
  form: WidgetForm;
  display: ReportWidgetDisplay;
  visibility: 'private' | 'agency';
  created_by: string;
}

export interface ReportWidgetPayload {
  name: string;
  report_definition_id: string | null;
  system_report_key: string | null;
  form: WidgetForm;
  display: ReportWidgetDisplay;
  visibility: 'private' | 'agency';
}

export interface DashboardTab {
  id: string;
  name: string;
  position: number;
}

export type DashboardTabWidgetStatus = 'ok' | 'no_access' | 'report_not_shared';

export interface DashboardTabWidgetItem {
  widget_id: string;
  name: string;
  created_by: string;
  status: DashboardTabWidgetStatus;
  widget: ReportWidget | null;
  report: ReportListItem | null;
}
